import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon, WarningIcon } from './icons';
import { Button } from './Button';
import {
  ANNOUNCE_CLEAR_MS, MAX_TOASTS, addToast, announcement, createToast, createToastTimers, removeToast,
} from '../utils/toastQueue.js';
import './Toast.css';

const ToastContext = createContext(() => null);

const ICONS = { success: CheckIcon, info: InfoIcon, warning: WarningIcon, error: AlertIcon };
const NO_ANNOUNCES = { polite: null, assertive: null };

const findToast = (region, id) => region?.querySelector(`[data-toast-id="${id}"]`) ?? null;

/**
 * Notifications discrètes en haut de l'écran, 3 au plus (les plus anciennes cèdent la place,
 * les erreurs en dernier). useToast() renvoie toast(message, { tone, duration, action }),
 * qui rend l'identifiant du toast :
 * - tone : 'success' (défaut), 'info', 'warning' ou 'error', chacun avec son icône ;
 * - duration : en ms, défaut selon le ton (une erreur reste 10 s) ; Infinity : jusqu'à fermeture ;
 * - action : { label, onClick } facultative, bouton qui ferme le toast.
 * Erreurs, avertissements et toasts avec action ont un bouton Fermer. Survol, focus ou onglet
 * masqué : pause. Lecteurs d'écran : erreurs annoncées en assertive, le reste en polite.
 * Clavier : F8 va au dernier toast, Échap ferme celui qui a le focus et rend le focus.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [announces, setAnnounces] = useState(NO_ANNOUNCES);
  const listRef = useRef([]);
  const seqRef = useRef(0);
  const regionRef = useRef(null);
  const hoverIdRef = useRef(null);
  const returnFocusRef = useRef(null);
  const dismissRef = useRef(() => {});
  const timersRef = useRef(null);
  if (!timersRef.current) timersRef.current = createToastTimers({ onExpire: (id) => dismissRef.current(id) });
  const timers = timersRef.current;

  const commit = useCallback((list) => {
    listRef.current = list;
    setToasts(list);
  }, []);

  const isShown = (id) => listRef.current.some((t) => t.id === id);

  const dismiss = useCallback((id) => {
    if (!listRef.current.some((t) => t.id === id)) return;
    const hadFocus = !!findToast(regionRef.current, id)?.contains(document.activeElement);
    timers.clear(id);
    timers.release(`hover:${id}`);
    if (hoverIdRef.current === id) hoverIdRef.current = null;
    commit(removeToast(listRef.current, id));
    if (hadFocus) {
      // Focus rendu là où il était avant F8 (ou avant l'entrée dans les notifications)
      const back = returnFocusRef.current;
      returnFocusRef.current = null;
      timers.release('focus');
      if (back?.isConnected && typeof back.focus === 'function') back.focus();
      else document.activeElement?.blur?.();
    }
  }, [commit, timers]);
  dismissRef.current = dismiss;

  const show = useCallback((message, options) => {
    seqRef.current += 1;
    const toast = createToast(message, options, seqRef.current);
    if (!toast) return null;
    // Toast survolé ou qui a le focus : jamais retiré pour faire de la place
    const focused = regionRef.current?.contains(document.activeElement)
      ? document.activeElement.closest('[data-toast-id]')
      : null;
    const keep = [hoverIdRef.current, focused ? Number(focused.dataset.toastId) : null].filter((id) => id !== null);
    const { list, id, removed } = addToast(listRef.current, toast, { max: MAX_TOASTS, keep });
    removed.forEach((removedId) => {
      timers.clear(removedId);
      timers.release(`hover:${removedId}`);
    });
    commit(list);
    timers.start(id, toast.duration);
    setAnnounces((prev) => ({ ...prev, [toast.politeness]: { key: toast.id, text: announcement(toast) } }));
    return id;
  }, [commit, timers]);

  // Onglet masqué : pause ; F8 : aller au dernier toast (action, Fermer ou le toast lui-même)
  useEffect(() => {
    const onVisibility = () => (document.hidden ? timers.hold('hidden') : timers.release('hidden'));
    const onKeyDown = (event) => {
      if (event.key !== 'F8' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const last = listRef.current[listRef.current.length - 1];
      const node = last ? findToast(regionRef.current, last.id) : null;
      if (!node) return;
      event.preventDefault();
      if (!regionRef.current.contains(document.activeElement)) returnFocusRef.current = document.activeElement;
      (node.querySelector('button') ?? node).focus();
    };
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [timers]);

  useEffect(() => () => timers.clearAll(), [timers]);

  // Zones d'annonce vidées après lecture : rien de périmé à retrouver au clavier
  useEffect(() => {
    const handles = Object.entries(announces)
      .filter(([, entry]) => entry)
      .map(([politeness, entry]) => setTimeout(() => {
        setAnnounces((prev) => (prev[politeness]?.key === entry.key ? { ...prev, [politeness]: null } : prev));
      }, ANNOUNCE_CLEAR_MS));
    return () => handles.forEach(clearTimeout);
  }, [announces]);

  const onRegionFocus = (event) => {
    if (regionRef.current?.contains(event.relatedTarget)) return;
    if (!returnFocusRef.current && event.relatedTarget) returnFocusRef.current = event.relatedTarget;
    timers.hold('focus');
  };

  const onRegionBlur = (event) => {
    if (regionRef.current?.contains(event.relatedTarget)) return;
    returnFocusRef.current = null;
    timers.release('focus');
  };

  const onRegionKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const node = event.target.closest?.('[data-toast-id]');
    if (!node) return;
    event.stopPropagation();
    dismiss(Number(node.dataset.toastId));
  };

  const runAction = (toast) => {
    if (!isShown(toast.id)) return;
    dismiss(toast.id);
    toast.action.onClick();
  };

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div
        ref={regionRef}
        className="toast-region"
        role={toasts.length ? 'region' : undefined}
        aria-label={toasts.length ? 'Notifications (F8)' : undefined}
        onFocus={onRegionFocus}
        onBlur={onRegionBlur}
        onKeyDown={onRegionKeyDown}
      >
        <AnimatePresence initial={false}>
          {toasts.map((toast) => {
            const Icon = ICONS[toast.tone];
            const messageId = `toast-message-${toast.id}`;
            return (
              <motion.div
                key={toast.id}
                layout
                data-toast-id={toast.id}
                className={`toast toast--${toast.tone}${toast.dismissible ? ' toast--dismissible' : ''}`}
                tabIndex={-1}
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.15 } }}
                transition={{ duration: 0.2 }}
                onPointerEnter={() => {
                  if (!isShown(toast.id)) return;
                  hoverIdRef.current = toast.id;
                  timers.hold(`hover:${toast.id}`);
                }}
                onPointerLeave={() => {
                  if (hoverIdRef.current === toast.id) hoverIdRef.current = null;
                  timers.release(`hover:${toast.id}`);
                }}
              >
                <Icon size={15} className="toast__icon" />
                <span id={messageId} className="toast__message">{toast.message}</span>
                {toast.action && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="toast__action"
                    aria-describedby={messageId}
                    onClick={() => runAction(toast)}
                  >
                    {toast.action.label}
                  </Button>
                )}
                {toast.dismissible && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={CloseIcon}
                    iconOnly
                    className="toast__close"
                    aria-label="Fermer la notification"
                    aria-describedby={messageId}
                    title="Fermer"
                    onClick={() => dismiss(toast.id)}
                  />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      {/* Annonces : zones présentes dès le chargement, sinon leur premier contenu est ignoré */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announces.polite && <p key={announces.polite.key}>{announces.polite.text}</p>}
      </div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {announces.assertive && <p key={announces.assertive.key}>{announces.assertive.text}</p>}
      </div>
    </ToastContext.Provider>
  );
}

/** toast(message, { tone, duration, action }) : voir ToastProvider. */
export function useToast() {
  return useContext(ToastContext);
}
